/* 탐색 피드 목록 — 탭(추천·인기·최신·좋아요)마다 같은 작품 묶음을 다른 순서로 보여 준다.
   지표(좋아요·대화 수)와 공개일은 백엔드 작품(work.stats · work.publishedAt)이 원천이다.
   값이 아직 오지 않으면 그 기준으로는 순서를 가를 수 없으므로 추천 순서를 그대로 둔다(지어낸 숫자로 줄 세우지 않는다). */
import { workFor } from '@/features/veluvit/lib/rules'

import type { Catalog } from '@/features/veluvit/types/catalog.types'

export type FeedTab = 'pick' | 'hot' | 'new' | 'liked'

export const FEED_TABS: { key: FeedTab; label: string }[] = [
  { key: 'pick', label: '추천' },
  { key: 'hot', label: '인기' },
  { key: 'new', label: '최신' },
  { key: 'liked', label: '좋아요' },
]

/** 작품 지표 — 서버 작품과 그 작품을 투영한 화면 모델의 값만 사용한다. */
export function workStats(catalog: Catalog, id: string) {
  const stats = workFor(catalog, id)?.stats
  const c = catalog.chars[id]
  return {
    likes: stats?.likes ?? c?.likes ?? 0,
    chats: stats?.chats ?? c?.chats ?? 0,
  }
}

/** 공개 시각(ms) — 서버 publishedAt. 없으면 null(탐색 최신 탭 · 작품 랭킹 신작이 쓴다) */
export const publishedTime = (catalog: Catalog, id: string) => {
  const t = Date.parse(workFor(catalog, id)?.publishedAt || '')
  return Number.isFinite(t) ? t : null
}

/**
 * 탭별 목록. pick 은 노출 순서(성향 필터 적용) 그대로.
 * hot: 좋아요 → 대화 수 순. new: 공개일 최신 순, 공개일 없는 작품은 뒤로. liked: 최근에 누른 것부터(서버 favorites 는 누른 순서로 쌓인다).
 * 정렬은 안정 정렬이라 기준 값이 같으면 추천 순서를 따른다.
 */
export function feedList(
  catalog: Catalog,
  pick: string[],
  favorites: string[],
  tab: FeedTab
): string[] {
  if (tab === 'liked') {
    // 성향 필터 밖이어도 내가 좋아요한 작품은 보여 준다 — 백엔드가 발행한 작품이기만 하면
    return favorites
      .slice()
      .reverse()
      .filter((id) => !!catalog.chars[id] && !!workFor(catalog, id))
  }
  if (tab === 'hot') {
    const stat = new Map(pick.map((id) => [id, workStats(catalog, id)]))
    return pick
      .slice()
      .sort(
        (a, b) =>
          stat.get(b)!.likes - stat.get(a)!.likes ||
          stat.get(b)!.chats - stat.get(a)!.chats
      )
  }
  if (tab === 'new') {
    const time = new Map(pick.map((id) => [id, publishedTime(catalog, id)]))
    return pick.slice().sort((a, b) => {
      const ta = time.get(a)
      const tb = time.get(b)
      if (ta == null || tb == null)
        return ta == null ? (tb == null ? 0 : 1) : -1
      return tb - ta
    })
  }
  return pick
}

/** 최근 2주 안에 공개된 작품 — 카드에 NEW 표시 */
export function isFresh(catalog: Catalog, id: string, now = Date.now()) {
  const t = publishedTime(catalog, id)
  return t != null && now - t < 14 * 24 * 60 * 60 * 1000
}
